import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';

const SearchAutocomplete = ({
  placeholder,
  value,
  options = [],
  onSelect,
  className = "",
  inputClassName = "",
  dropdownClassName = "",
  disabled = false
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const containerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const storageKey = `search_pref_${(placeholder || 'default').toLowerCase().trim()}`;

  const getLocalSearchScore = (key) => {
    try {
      const data = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return data[String(key).toLowerCase()] || 0;
    } catch {
      return 0;
    }
  };

  const incrementLocalSearchScore = (key) => {
    try {
      const data = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const k = String(key).toLowerCase();
      data[k] = (data[k] || 0) + 1;
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch {}
  };

  const getItemLabel = (opt) => {
    if (opt === null || opt === undefined) return '';
    if (typeof opt === 'object') return opt.name || opt.label || String(opt.id || '');
    return String(opt);
  };

  const getItemValue = (opt) => {
    if (opt === null || opt === undefined) return '';
    if (typeof opt === 'object') return opt.id !== undefined ? opt.id : opt.name || opt.label || '';
    return String(opt);
  };

  const getItemScore = (opt) => {
    const label = getItemLabel(opt);
    const localScore = getLocalSearchScore(label);
    const objScore = (typeof opt === 'object' && (opt.viewCount || opt.searchCount || opt.count || 0)) || 0;
    return (localScore * 10) + objScore;
  };

  const getMatchTier = (label, q) => {
    if (!label || !q) return 99;
    const clean = label.toLowerCase();
    const queryLower = q.toLowerCase();

    // Tier 0: Exact match
    if (clean === queryLower) return 0;

    // Tier 1: Starts with query (Prefix match e.g., 'Bhuj' starts with 'b')
    if (clean.startsWith(queryLower)) return 1;

    // Tier 2: Any individual word starts with query (Word boundary match e.g. 'South Bangalore' -> 'b')
    const words = clean.split(/[\s\-_/,]+/);
    if (words.some(w => w.startsWith(queryLower))) return 2;

    // Tier 3: Contains query anywhere inside
    if (clean.includes(queryLower)) return 3;

    return 99;
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Sync internal query text when value prop changes externally (and user is not actively typing)
  useEffect(() => {
    if (isTyping) return;
    if (value === '' || value === null || value === undefined) {
      setQuery('');
      return;
    }
    const matchedOption = options.find(opt => {
      if (typeof opt === 'object' && opt !== null) {
        return String(opt.id) === String(value) || opt.name?.toLowerCase() === String(value).toLowerCase();
      }
      return String(opt).toLowerCase() === String(value).toLowerCase();
    });

    if (matchedOption) {
      setQuery(typeof matchedOption === 'object' ? matchedOption.name : String(matchedOption));
    } else {
      const isNumericId = /^\d+$/.test(String(value).trim());
      if (isNumericId && options.length === 0) {
        setQuery('');
      } else {
        setQuery(String(value));
      }
    }
  }, [value, options, isTyping]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setIsTyping(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter and Rank options by Prefix Match Tier + Popularity / Score
  const processedOptions = useMemo(() => {
    if (!Array.isArray(options) || options.length === 0) return [];
    const q = query.trim().toLowerCase();

    // Initial click / empty search: Sort options by popularity / score (descending)
    if (!isTyping || !q) {
      return [...options].sort((a, b) => {
        const scoreA = getItemScore(a);
        const scoreB = getItemScore(b);
        if (scoreB !== scoreA) return scoreB - scoreA;
        return getItemLabel(a).localeCompare(getItemLabel(b));
      });
    }

    // Active typing with search query:
    const matches = [];
    for (const opt of options) {
      const label = getItemLabel(opt);
      const tier = getMatchTier(label, q);
      if (tier < 99) {
        matches.push({
          opt,
          label,
          tier,
          score: getItemScore(opt)
        });
      }
    }

    matches.sort((a, b) => {
      // 1. Match Tier (Starts-with first, then word-boundary, then contains)
      if (a.tier !== b.tier) {
        return a.tier - b.tier;
      }
      // 2. Score (Popularity / most viewed within the same tier)
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      // 3. Shorter label length first
      if (a.label.length !== b.label.length) {
        return a.label.length - b.label.length;
      }
      // 4. Alphabetical fallback
      return a.label.localeCompare(b.label);
    });

    return matches.map(m => m.opt);
  }, [options, query, isTyping, updateCount]);

  // Display top 5-6 on initial open/focus, or top matching suggestions when typing
  const displayedOptions = useMemo(() => {
    if (isTyping && query.trim() !== '') {
      return processedOptions.slice(0, 15);
    }
    return processedOptions.slice(0, 6);
  }, [processedOptions, isTyping, query]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setIsTyping(true);
    setQuery(val);
    setIsOpen(true);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const exactMatch = options.find(opt => {
        const label = getItemLabel(opt);
        return label.toLowerCase() === val.trim().toLowerCase();
      });

      if (exactMatch) {
        const matchVal = getItemValue(exactMatch);
        const matchLabel = getItemLabel(exactMatch);
        if (onSelect) onSelect(matchVal, matchLabel, exactMatch);
      }
    }, 200);
  };

  const handleSelectOption = (opt) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const label = getItemLabel(opt);
    const val = getItemValue(opt);
    setQuery(label);
    setIsTyping(false);
    setIsOpen(false);
    incrementLocalSearchScore(label);
    if (typeof opt === 'object' && opt.name) {
      incrementLocalSearchScore(opt.name);
    }
    setUpdateCount(c => c + 1);
    if (onSelect) {
      onSelect(val, label, opt);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setQuery('');
    setIsTyping(false);
    setIsOpen(false);
    if (onSelect) {
      onSelect('', '', null);
    }
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="relative flex items-center w-full">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onClick={() => {
            if (!isOpen) {
              setIsOpen(true);
              setIsTyping(false);
            }
          }}
          onFocus={() => {
            setIsOpen(true);
            setIsTyping(false);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`truncate ${inputClassName}`}
        />
        {query ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-1 sm:right-3 text-gray-400 hover:text-gray-600 p-0.5 rounded-full cursor-pointer focus:outline-none flex-shrink-0"
          >
            <X size={12} className="sm:w-3.5 sm:h-3.5" />
          </button>
        ) : (
          <ChevronDown
            size={12}
            className={`absolute right-1 sm:right-3 text-gray-400 pointer-events-none transition-transform duration-200 sm:w-3.5 sm:h-3.5 ${isOpen ? 'rotate-180 text-[#2ab391]' : ''}`}
          />
        )}
      </div>

      {isOpen && !disabled && (
        <div className={`absolute top-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto z-[9999] py-1 text-left ${dropdownClassName || 'left-0 right-0'}`}>
          {displayedOptions.length > 0 ? (
            displayedOptions.map((opt, idx) => {
              const label = getItemLabel(opt);
              const val = getItemValue(opt);
              const isSelected = String(value).toLowerCase() === String(val).toLowerCase() || String(value).toLowerCase() === label.toLowerCase();
              return (
                <div
                  key={idx}
                  onClick={() => handleSelectOption(opt)}
                  className={`px-3.5 py-2.5 text-xs sm:text-sm font-medium cursor-pointer transition-colors flex items-center justify-between whitespace-nowrap ${
                    isSelected ? 'bg-[#eaf7f4] text-[#2ab391] font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-[#2ab391]'
                  }`}
                >
                  <span className="truncate">{label}</span>
                </div>
              );
            })
          ) : (
            <div className="px-4 py-3 text-xs text-gray-400 italic text-center">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
